import { type VoidFunctionComponent } from 'react';
import { Helmet } from 'react-helmet';
import { browserHistory } from 'react-router';

import { useFetchReleaseWithCallback } from '../../hooks/useFetchRelease';
import { useFetchMinimalProjectsAndUpdateState } from '../../hooks/useMinimalProjects';
import useReleaseGroupState from '../../hooks/useReleaseGroupState';
import useUpdateRelease from '../../hooks/useUpdateRelease';
import ErrorBanner from '../../shared/ErrorBanner';
import ProjectsPage from '../../shared/ProjectsPage/ProjectsPage';
import type {
  CreateOrUpdateProjectGroupReleaseProjectRequest,
  UpdateProjectGroupReleaseRequest,
} from '../../../../../../../typings/api';
import type { ProjectGroupRelease, RouterProps } from '../../../../../../types';

import styles from '../../shared/ReleaseGroupsRevamp.module.less';

type Props = RouterProps<{ projectGroupId: string; releaseId: string }>;

const EditReleaseContainer: VoidFunctionComponent<Props> = ({ params }) => {
  const groupId = params.projectGroupId;
  const releaseId = params.releaseId;

  const state = useReleaseGroupState();

  const { onAddAllProjects, onRemoveAllProjects } = useFetchMinimalProjectsAndUpdateState(
    state.addMinimalProjects,
    state.removeProjects,
    state.filters,
  );

  //  This callback is called after (a non-null) release is fetched.
  const handleFetchRelease = (release: ProjectGroupRelease) => {
    state.setVersion(release.title);

    state.addReleaseGroupProjects(release.projects);
    state.setFilters({
      locators: release.projects.map((project) => project.projectId),
    });
  };

  const { release } = useFetchReleaseWithCallback(groupId, releaseId, handleFetchRelease);

  const onCancel = () => {
    browserHistory.push(`/projects/group/${groupId}/releases`);
  };

  const { isUpdatingRelease, onUpdateRelease, errors, onResetErrors } = useUpdateRelease();

  const onSave = () => {
    if (!release) {
      return;
    }

    const existingReleaseProjects = release.projects.map((project) => project.projectId) ?? [];
    const req: Required<UpdateProjectGroupReleaseRequest> = {
      title: state.version,
      projects: [],
      projectsToDelete: [],
    };

    // Check for any new / updated projects.
    Object.values(state.addedProjects).forEach((project) => {
      // If this project has no revision selected, skip it.
      if (!project.revision) {
        return;
      }

      const updatedReleaseProject: CreateOrUpdateProjectGroupReleaseProjectRequest = {
        projectId: project.locator,
        branch: project.branch,
        revisionId: project.revision.locator,
      };

      req.projects.push(updatedReleaseProject);
    });

    // Check for any deleted projects. A project has been deleted if it is present in
    // the existing release but NOT present in the index of added projects.
    existingReleaseProjects.forEach((project) => {
      if (!(project in state.addedProjects)) {
        req.projectsToDelete.push(project);
      }
    });

    onUpdateRelease(groupId, releaseId, req);
  };

  return (
    <>
      <Helmet
        title={release?.title}
        titleTemplate="%s - Edit Release | FOSSA"
        defaultTitle="Edit Release | FOSSA"
      />
      {errors && (
        <div className={styles.banner}>
          <ErrorBanner
            title="An error occurred while editing this release."
            body={errors.join('. ')}
            onDismiss={onResetErrors}
          />
        </div>
      )}
      <ProjectsPage>
        <ProjectsPage.Header
          pageTitle={`Edit ${release?.title || 'release'}`}
          saveButtonText="Save"
          isLoading={isUpdatingRelease}
          validationErrors={state.validate()}
          onCancel={onCancel}
          onSave={onSave}
        />
        <ProjectsPage.Details
          title="Details"
          description="Update the release version"
          name={state.name}
          version={state.version}
          setName={state.setName}
          setVersion={state.setVersion}
        />
        <ProjectsPage.Projects
          shouldShowOnlyAddedProjects={state.shouldShowOnlyAddedProjects}
          setShouldShowOnlyAddedProjects={state.setShouldShowOnlyAddedProjects}
          areProjectsLoading={state.areProjectsLoading}
          projectCount={state.projectCount}
          projects={state.projects}
          projectErrors={state.projectErrors}
          addedProjects={state.addedProjects}
          onUpdateAddedProject={state.updateAddedProject}
          onAddProject={state.addProjects}
          onRemoveProject={state.removeProjects}
          onAddAllProjects={onAddAllProjects}
          onRemoveAllProjects={onRemoveAllProjects}
          filters={state.filters}
          setFilters={state.setFilters}
          onSelectLatestRevisionForSelectedProjects={state.selectLatestRevisionForSelectedProjects}
          sort={state.sort}
          setSort={state.setSort}
          page={state.page}
          setPage={state.setPage}
          count={state.count}
          setCount={state.setCount}
        />
        <ProjectsPage.Footer
          saveButtonText="Save"
          validationErrors={state.validate()}
          onCancel={onCancel}
          onSave={onSave}
          isLoading={isUpdatingRelease}
        />
      </ProjectsPage>
    </>
  );
};

export default EditReleaseContainer;
